import React, { useState, useEffect } from 'react';

function AdminPage() {
    const [firmen, setFirmen] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');


    // Filter firmen based on search term
    const filteredFirmen = firmen.filter(firma =>
        (firma.firmenname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (firma.kontaktperson || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (firma.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (firma.adresse || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (firma.telefon || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.dropdown-container')) {
                setActiveDropdown(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    useEffect(() => {
        const fetchFirmen = async () => {
            try {
                setLoading(true);
                const response = await fetch('https://company-application-platform-backend.onrender.com/api/firma', {
                    method: 'GET',
                    headers: { 
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    credentials: 'include'
                });
    
                if (response.ok) {
                    const data = await response.json();
                    setFirmen(data);
                    setError(null);
                } else {
                    setError('Fehler beim Laden der Firmen: ' + response.status);
                }
            } catch (error) {
                setError('Netzwerkfehler: ' + error.message);
            } finally {
                setLoading(false);
            }
        };
    
        fetchFirmen();
    }, []);
    

    const handleStatusChange = async (firmaId, status) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`https://company-application-platform-backend.onrender.com/api/firma/${firmaId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include',
                body: JSON.stringify({ status })
            });

            if (response.ok) {
                const updatedFirma = await response.json();
                setFirmen(firmen.map(firma =>
                    firma.id === firmaId ? { ...firma, status: updatedFirma.status } : firma
                ));
                setSuccess('Status erfolgreich aktualisiert!');
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError('Fehler beim Aktualisieren des Status');
            }
        } catch (error) {
            setError('Netzwerkfehler: ' + error.message);
        }
    };

    const handleDeleteFirma = async (firmaId) => {
        if (!window.confirm('Bist du sicher, dass du diese Firma löschen möchtest?')) return;
        
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`https://company-application-platform-backend.onrender.com/api/firma/${firmaId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include'
            });
            
            if (response.ok) {
                setFirmen(firmen.filter(firma => firma.id !== firmaId));
                setSuccess('Firma erfolgreich gelöscht!');
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError('Fehler beim Löschen der Firma');
            }
        } catch (error) {
            setError('Netzwerkfehler: ' + error.message);
        }
    };

    const getStatusBadgeClass = (status) => {
        switch(status) {
            case 'offen': return 'status-badge status-offen';
            case 'in Arbeit': return 'status-badge status-in-arbeit';
            case 'versendet': return 'status-badge status-versendet';
            default: return 'status-badge';
        }
    };

    const toggleDropdown = (firmaId, event) => {
        event.stopPropagation();
        setActiveDropdown(activeDropdown === firmaId ? null : firmaId);
    };

    return (
        <div>
            <section className="hero-section py-12">
                <div className="container mx-auto px-4">
                    <h1>Öffentliche Firmenliste verwalten</h1>
                    <p className="lead">
                        Verwalten Sie alle öffentlich registrierten Firmen und deren Status.
                    </p>
                </div>
            </section>

            <section className="page-content-section py-8">
                <div className="container mx-auto px-4">
                    <div className="max-w-7xl mx-auto">
                        {/* Header */}
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">
                                Öffentliche Firmen ({firmen.length})
                                {searchTerm && ` (${filteredFirmen.length} gefiltert)`}
                            </h2>
                        </div>
                        Keine Firmen registriert
                        {/* Search Bar */}
                        <div className="mb-6">
                            <div className="search-input-container">
                                <div className="search-input-wrapper">
                                    <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                                    </svg>
                                    <input
                                        type="text"
                                        placeholder="Firma, Kontakt, E-Mail oder Adresse suchen..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="search-input"
                                    />
                                    {searchTerm && (
                                        <button
                                            onClick={() => setSearchTerm('')}
                                            className="search-clear-btn"
                                            type="button"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Messages */}
                        {error && <div className="error-message">{error}</div>}
                        {success && <div className="success-message">{success}</div>}

                        {/* Firmen Table */}
                        {loading && firmen.length === 0 ? (
                            <div className="flex justify-center items-center p-12">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                            </div>
                        ) : (searchTerm ? filteredFirmen : firmen).length === 0 ? (
                            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    {searchTerm ? 'Keine passenden Firmen gefunden' : 'Keine Firmen registriert'}
                                </h3>
                                <p className="text-gray-500">
                                    {searchTerm ? 'Versuchen Sie einen anderen Suchbegriff' : 'Es sind noch keine Firmen registriert.'}
                                </p>
                            </div>
                        ) : (
                            <div className="bg-white rounded-lg shadow-lg">
                                <div className="overflow-x-auto">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Firmenname</th>
                                                <th>Adresse</th>
                                                <th>Kontaktperson</th>
                                                <th>E-Mail</th>
                                                <th>Telefon</th>
                                                <th>Status</th>
                                                <th className="text-center">Aktionen</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(searchTerm ? filteredFirmen : firmen).map((firma) => {
                                                const firmaId = firma.id || firma._id;
                                                return (
                                                    <tr key={firmaId}>
                                                        <td className="font-medium text-gray-900">
                                                            {firma.firmenname}
                                                        </td>
                                                        <td className="text-gray-700">
                                                            {firma.adresse}
                                                        </td>
                                                        <td className="text-gray-700">
                                                            {firma.kontaktperson || '-'}
                                                        </td>
                                                        <td className="text-gray-700">
                                                            <a href={`mailto:${firma.email}`} className="text-blue-600 hover:text-blue-800">
                                                                {firma.email}
                                                            </a>
                                                        </td>
                                                        <td className="text-gray-700">
                                                            <a href={`tel:${firma.telefon}`} className="text-blue-600 hover:text-blue-800">
                                                                {firma.telefon}
                                                            </a>
                                                        </td>
                                                        <td>
                                                            <span className={getStatusBadgeClass(firma.status)}>
                                                                {firma.status}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="dropdown-container">
                                                                <button 
                                                                    onClick={(e) => toggleDropdown(firmaId, e)}
                                                                    className="dropdown-trigger"
                                                                    disabled={loading}
                                                                >
                                                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                                                    </svg>
                                                                </button>
                                                                
                                                                {activeDropdown === firmaId && (
                                                                    <div className="dropdown-menu">
                                                                        <div className="px-3 py-2 border-b border-gray-100">
                                                                            <p className="text-xs font-medium text-gray-500"></p>
                                                                        </div>
                                                                        <div className="p-1">
                                                                            <button 
                                                                                onClick={() => handleStatusChange(firmaId, 'offen')}
                                                                                className={`dropdown-item ${firma.status === 'offen' ? 'bg-blue-50' : ''}`}
                                                                            >
                                                                                <span className="status-badge status-offen mr-2"></span>
                                                                                Offen
                                                                            </button>
                                                                            <button 
                                                                                onClick={() => handleStatusChange(firmaId, 'in Arbeit')}
                                                                                className={`dropdown-item ${firma.status === 'in Arbeit' ? 'bg-blue-50' : ''}`}
                                                                            >
                                                                                <span className="status-badge status-in-arbeit mr-2"></span>
                                                                                In Arbeit
                                                                            </button>
                                                                            <button 
                                                                                onClick={() => handleStatusChange(firmaId, 'versendet')}
                                                                                className={`dropdown-item ${firma.status === 'versendet' ? 'bg-blue-50' : ''}`}
                                                                            >
                                                                                <span className="status-badge status-versendet mr-2"></span>
                                                                                Versendet
                                                                            </button>
                                                                        </div>
                                                                        <div className="p-1 border-t border-gray-100">
                                                                            <button 
                                                                                onClick={() => handleDeleteFirma(firmaId)} 
                                                                                className="dropdown-item dropdown-item-delete"
                                                                            >
                                                                                Löschen
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}

export default AdminPage;